import React, { ChangeEvent } from 'react';

import { Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { WithTranslation, withTranslation } from 'react-i18next';
import Keyboard from 'react-simple-keyboard';
import { Services } from 'helpers';
import { observer, inject } from 'mobx-react';

import { WizardModel, WizardStep } from 'models/kiosk/WizardModel';
import { PrevButton, NextButton } from 'components/wizard';

import { Form, FormField } from 'models/form';
import validators, { ValidatorOptions } from 'validators/Index';
import { Billpay } from 'models/kiosk/Billpay';
import { ReceiptGeneratingErrorModal } from 'containers/modals';
import { StoreModel } from 'models/Index';
import { ThemeType } from 'interfaces';
import { RedditTextField } from 'components/inputs';

interface EmailAddressStepProps extends WithTranslation {}
interface EmailAddressStepPropsInjected extends EmailAddressStepProps {
    Store: StoreModel;
    wizard: WizardModel;
    billpay: Billpay;
}

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        padding: 'calc(10vh/100*5) 10% calc(10vh/100*5) 10%',
    },
    body: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginTop: '5vh',
    },
    title: {
        color: (theme: ThemeType) => theme.primaryColor,
        fontSize: '3.5rem',
        maxWidth: '700px',
        textAlign: 'center',
        lineHeight: '100px',
    },
    inputRoot: {
        width: '494px',
        alignSelf: 'center',
        marginBottom: '5vh',
    },
    inputTitle: {
        marginTop: '8.8vh',
        marginBottom: '48px',
        color: (theme: ThemeType) => theme.secondaryColor,
    },
    inputError: {
        marginLeft: 0,
        fontSize: '1.75rem',
    },

    keyboard: {
        width: '100% !important',
        alignSelf: 'center',
        backgroundColor: (theme: ThemeType) => theme.backgroundColor,
        paddingBottom: '5vh',

        '& .hg-row:not(:last-child)': {
            marginBottom: '16px !important',
        },
        '& .hg-row': {
            display: 'flex',
            justifyContent: 'center',
        },
    },
    keyboardButton: {
        border: (theme: ThemeType) =>
            `1px solid ${theme.secondaryButton.borderColor}`,
        boxShadow: (theme: ThemeType) =>
            `2px 2px 0px ${theme.secondaryButton.borderColor}`,
        borderRadius: '5px',
        width: '150px',
        height: '80px',
        fontSize: '3rem',
        fontWeight: 'bold',
        fontFamily: 'Altice',
        background: (theme: ThemeType) => theme.secondaryButton.backgroundColor,
        color: (theme: ThemeType) => theme.secondaryButton.fontColor,
        display: 'flex !important',
        alignItems: 'center',
        justifyContent: 'center',

        '&:not(:last-child)': {
            marginRight: '16px !important',
        },
        '&.hg-activeButton': {
            border: (theme: ThemeType) =>
                `2px solid ${theme.secondaryButton.borderColor}`,
            backgroundColor: (theme: ThemeType) =>
                theme.secondaryButton.backgroundColor,
            color: (theme: ThemeType) => theme.secondaryButton.fontColor,
        },
    },
    keyboardSpecialButton: {
        fontSize: '1.5rem',
    },

    EsKeyboardSpecialButton: {
        fontSize: '1rem',
        width: '100px',
    },
    footer: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
    },
});

const EmailAddressStep = observer<React.FC<EmailAddressStepProps>>((props) => {
    let emailAddressFieldName = 'EmailAddress';
    let clearKeyCode: string = '{clear}';
    const keyboard = React.useRef<Keyboard | null>(null);

    const [receiptGenerateFailed, setReceiptGenerateFailed] =
        React.useState(false);

    const injected = props as EmailAddressStepPropsInjected;
    const { t } = props;
    const { wizard, i18n, Store } = injected;

    const classes = useStyles(Store.deviceProfile.theme);
    const [form] = React.useState(
        new Form().initFromObject({
            [emailAddressFieldName]: [
                '',
                [
                    validators.required(),
                    validators.regex(
                        new RegExp(
                            /(^([0-9a-zA-Z]([-.\w]*[0-9a-zA-Z])*@([0-9a-zA-Z][-\w]*[0-9a-zA-Z]\.)+[a-zA-Z]{2,9})$)/
                        ),
                        new ValidatorOptions(true)
                    ),
                    validators.minlength(3, new ValidatorOptions(true)),
                ],
            ],
        })
    );

    React.useEffect(() => {
        //Focus initial input
        const field = form.fieldsObj[emailAddressFieldName];
        field && field.focus();
    });

    const fields = form.fieldsObj;

    const [keyboardLayout] = React.useState({
        default: [
            `1 2 3 4 5 6 7 8 9 0 {bksp}`,
            `q w e r t y u i o p _`,
            `a s d f g h j k l @ -`,
            `  z x c v b n m .com . ${clearKeyCode}`,
        ],
    });

    const [keyboardDisplay] = React.useState({
        '{bksp}': props.t('DEL'),
        [clearKeyCode]: props.t('CLEAR'),
    });

    const onChangeInput = (
        field: FormField,
        event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        let inputVal = event.target.value;

        field.setValue(inputVal);
        keyboard?.current?.setInput(inputVal);
    };

    const onChangeAll = (input: string) => {
        const field = form.fieldByName(emailAddressFieldName);
        field && field.setValue(input);
    };

    const onKeyPress = (e: any) => {
        //clear currenly active input
        if (e === clearKeyCode) {
            onChangeAll('');
            keyboard?.current?.clearInput(emailAddressFieldName);
        }
    };

    const prev = () => {
        const { wizard } = injected;
        wizard.goToStep(WizardStep.PrintReceipt);
    };

    const send = () => {
        if (!form.validateAll()) {
            return;
        }
        const { billpay } = injected;
        billpay.receipts
            .emailReceipt(form.fieldsObj[emailAddressFieldName].value)
            .catch((error) => {
                setReceiptGenerateFailed(true);
                throw error;
            })
            .then(() => wizard.goToStep(WizardStep.Farewell));
    };

    return (
        <div className={classes.root}>
            <div className={classes.body}>
                <div className={classes.title}>
                    <Typography variant='h2'>
                        {t('Enter Email Address')}
                    </Typography>
                </div>
                <Typography className={classes.inputTitle} variant='h4'>
                    {t('Enter your email address and press the Send button')}
                </Typography>
                <RedditTextField
                    inputRef={fields[emailAddressFieldName].ref}
                    onChange={(e: any) => {
                        onChangeInput(fields[emailAddressFieldName], e);
                    }}
                    className={classes.inputRoot}
                    inputProps={{ maxLength: '50' }}
                    FormHelperTextProps={{
                        classes: {
                            root: classes.inputError,
                        },
                    }}
                    variant='filled'
                    label={t('E-mail address')}
                    placeholder={'email@email.com'}
                    error={fields[emailAddressFieldName].hasError}
                    helperText={
                        fields[emailAddressFieldName].hasError &&
                        fields[emailAddressFieldName].error
                    }
                    value={fields[emailAddressFieldName].value}
                />
                <Keyboard
                    keyboardRef={(r: any) => (keyboard.current = r)}
                    inputName={emailAddressFieldName}
                    layout={keyboardLayout}
                    layoutName={'default'}
                    display={keyboardDisplay}
                    theme={classes.keyboard}
                    preventMouseDownDefault={true}
                    disableButtonHold={true}
                    maxLength={{
                        [emailAddressFieldName]: 50,
                    }}
                    buttonTheme={[
                        {
                            class: classes.keyboardButton,
                            buttons: `{bksp} ${clearKeyCode} 1 2 3 4 5 6 7 8 9 0 q w e r t y u i o p _ a s d f g h j k l @ - z x c v b n m .com .`,
                        },
                        {
                            class:
                                i18n.language === 'en'
                                    ? classes.keyboardSpecialButton
                                    : classes.EsKeyboardSpecialButton,
                            buttons: `{bksp} ${clearKeyCode} .com`,
                        },
                    ]}
                    onKeyPress={onKeyPress}
                    onChange={onChangeAll}
                />
            </div>
            <div className={classes.footer}>
                <PrevButton
                    buttonProps={{
                        onClick: prev,
                    }}
                />
                <NextButton
                    title={t('Send')}
                    buttonProps={{
                        disabled: !form.hasChanges,
                        onClick: send,
                    }}
                />
            </div>

            <ReceiptGeneratingErrorModal
                modalProps={{
                    open: receiptGenerateFailed,
                    onClose: () => {
                        wizard.goToStep(WizardStep.Farewell);
                    },
                }}
                buttonProps={{
                    onClick: () => {
                        wizard.goToStep(WizardStep.Farewell);
                    },
                }}
            />
        </div>
    );
});

export default withTranslation()(
    inject(
        Services.STORE,
        Services.PAYMENT_WIZARD,
        Services.BILLPAY
    )(EmailAddressStep)
);
